// ─────────────────────────────────────────────────────────────
// ARTS MARTIAUX ENFANTS — COURS PRÊTS™ (version française de Martial Arts Kids Class Ready™) — tous les textes et liens sont ici.
// Images : data/images.ts · Pixel : data/pixel.ts · Prix selon la région du visiteur (EUR / CHF / CAD) : utils/currency.ts
// Copie exacte de la landing anglaise (même structure, même palette, mêmes sections, même logique de conversion).
// Promesse (doit correspondre aux pubs, au checkout et au PDF) : programme de 52 semaines + 10 cours enfants complets de 45 minutes,
// paiement unique, satisfait ou remboursé pendant 10 jours, les 5 bonus dans l’ordre du checkout.
// Valeurs EUR / CHF : livre 159 ; bonus 37, 27, 27, 27, 37 (155) ; total 314 → 27, économie 287.
// Valeurs CAD : livre 219 ; bonus 49, 37, 37, 37, 49 (209) ; total 428 → 37, économie 391.
// Enfants + sport de contact : aucune promesse d’autodéfense, de grades ou d’inscriptions ; pattes d’ours et dans le vide, pas de combat ; pas de témoignages.
// ─────────────────────────────────────────────────────────────
import { prices } from '../utils/currency';

/** Typographie française : espace insécable avant ? ! : ; » et après « (jamais seuls en début de ligne sur mobile). */
const NBSP = ' ';
function fr<T>(value: T): T {
  if (typeof value === 'string') return value.replace(/ ([?!:;»])/g, `${NBSP}$1`).replace(/« /g, `«${NBSP}`) as unknown as T;
  if (Array.isArray(value)) return value.map((v) => fr(v)) as unknown as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as unknown as Record<string, unknown>)) out[k] = fr(v);
    return out as unknown as T;
  }
  return value;
}

export { META_PIXEL_ID } from './pixel';
export const CHECKOUT_URL = 'https://arts-martiaux-enfants-pret.impultienda.ar/checkout';

export const PAGE_TITLE = 'Arts Martiaux Enfants — Cours Prêts™ | Toute votre année de cours enfants, déjà planifiée';
export const META_DESCRIPTION =
  'Un programme de 52 semaines et 10 cours d’arts martiaux pour enfants de 45 minutes, complets et prêts à donner. Ouvrez la page de la semaine, préparez en 5 minutes et donnez le cours ce soir.';

// ── GLOBAL ───────────────────────────────────────────────────
export const brand = 'ARTS MARTIAUX ENFANTS — COURS PRÊTS™';
/** Texte de tous les boutons qui descendent jusqu’à l’offre */
export const ctaLabel = 'JE PLANIFIE TOUTE MON ANNÉE';
export const ctaMicrocopy = `PDF immédiat · Paiement unique de ${prices.main} · Satisfait ou remboursé pendant 10 jours`;
export const stickyLabel = `PLANIFIER MON ANNÉE · ${prices.main}`;
export const topBarMobileLabel = `LE LIVRE · ${prices.main}`;
export const price = prices.main;

// ── 1. HERO (public visé, titre, sous-titre court, mockup, bouton) ──
export const hero = fr({
  eyebrow: 'POUR LES PROFS DE KARATÉ, TAEKWONDO, KICK-BOXING ET BOXE THAÏ',
  titleLead: 'TOUTE VOTRE ANNÉE DE COURS ENFANTS,',
  titleHighlight: 'DÉJÀ PLANIFIÉE.',
  sub: 'Programme de 52 semaines + 10 cours enfants complets de 45 minutes, prêts à donner. Ouvrez la page de la semaine et donnez le cours ce soir.',
  mockupAlt: 'Arts Martiaux Enfants — Cours Prêts™ sur un écran d’ordinateur, avec les 5 bonus sur tablettes et téléphones',
});

// ── 2. CHIFFRES ─────────────────────────────────────────────────────
export const stats = fr([
  { value: '52', label: 'SEMAINES PLANIFIÉES' },
  { value: '10', label: 'COURS COMPLETS' },
  { value: '45', label: 'MINUTES CHRONOMÉTRÉES' },
  { value: '3', label: 'NIVEAUX D’ÂGE DANS CHAQUE COURS' },
]);

// ── 2b. AVANT / APRÈS ──────────────────────────────────────────────
export const beforeAfter = fr({
  title: 'FINI LES COURS PRÉPARÉS LA VEILLE AU SOIR.',
  before: {
    label: 'AVANT',
    when: 'La veille au soir',
    quote: '« Qu’est-ce que je fais faire aux enfants ce soir ? »',
    items: ['À chercher des exercices en vidéo.', 'Encore le même exercice de coups de poing.', 'Les petits perdus, les grands qui s’ennuient.'],
  },
  after: {
    label: 'APRÈS',
    when: 'Sur le tatami',
    items: ['Ouvrez la page de la semaine.', 'Préparez en 5 minutes.', 'Donnez le cours de 45 minutes, minute par minute.', 'Salut de fin, puis envoyez le mot aux parents.'],
  },
});

// ── 3. DE QUOI VOTRE COURS A-T-IL BESOIN CETTE SEMAINE (choisir un besoin, voir le cours prêt) ──
const block =
  'Déroulé en minutes : salut et jeu d’échauffement 8 · technique 12 · exercices aux pattes d’ours et à deux 12 · jeu final 8 · retour au calme et valeur de la semaine 5';
export const selector = fr({
  eyebrow: 'PARTEZ DU NIVEAU DE VOS ÉLÈVES',
  title: 'DE QUOI VOTRE COURS A-T-IL BESOIN CETTE SEMAINE ?',
  sub: 'Choisissez un besoin et découvrez le cours prêt pour y répondre.',
  startLabel: 'VOTRE PROCHAIN COURS',
  tablistLabel: 'Besoins du cours',
  columns: ['PAGE 1 · PLAN DU COURS', 'PAGE 2 · ENSEIGNER', 'PAGE 3 · EXERCICES ET JEU'],
  note: 'Les 10 cours suivent cette même structure en 3 pages. Adaptez chacun à votre discipline et aux règles de sécurité de votre club.',
  items: [
    {
      tab: 'Bien démarrer : garde et respect',
      n: 'COURS 01 · SEMAINE 1',
      title: 'Garde, posture et respect du dojo',
      goal: 'L’enfant salue, se met dans une garde solide, les mains hautes, et revient en position prêt au commandement.',
      open: [
        'Objectif du cours, matériel et préparation en 5 minutes',
        'Vos premiers mots : « Aujourd’hui, nous apprenons à être forts et calmes. Garde ta posture droite, tes mains prêtes et ton regard attentif. »',
        block,
      ],
      activity: [
        '5 étapes techniques avec points clés : garde-à-vous, salut, position prêt, position de garde, garde haute',
        '3 consignes « À corriger », comme « Des rails de train, pas un fil de funambule ! »',
        'Fiche niveau d’âge : comment l’enseigner aux 4–6 ans, 7–9 ans et 10–12 ans',
      ],
      close: [
        '2 exercices avec schéma du tatami : La revue des gardes et Sautille et fige-toi',
        'Jeu final : Le dojo a dit',
        'Échange sur la valeur (le respect), mot aux parents et contrôle des barrettes',
      ],
    },
    {
      tab: 'Des coups de pied avec équilibre',
      n: 'COURS 03 · SEMAINE 9',
      title: 'Coup de pied de face et équilibre',
      goal: 'L’enfant lève le genou, frappe devant avec le bol du pied et repose le pied au sol en gardant l’équilibre.',
      open: [
        'Objectif du cours, matériel et préparation en 5 minutes',
        'Vos premiers mots : « Genou levé… frappe… genou ramené… pied au sol. La garde reste haute ! »',
        block,
      ],
      activity: [
        'Étapes techniques avec points clés, du genou levé au pied reposé',
        '3 consignes « À corriger » pour les erreurs de coup de pied les plus fréquentes',
        'Fiche niveau d’âge pour les 4–6 ans, 7–9 ans et 10–12 ans',
      ],
      close: [
        '2 exercices avec schémas et durées : Coups de pied au bouclier et L’équilibriste',
        'Jeu final : Feu rouge, feu vert',
        'Échange sur la valeur (la maîtrise de soi), mot aux parents et contrôle des barrettes',
      ],
    },
    {
      tab: 'Des enchaînements aux pattes d’ours',
      n: 'COURS 06 · SEMAINE 22',
      title: 'Enchaînements de poings aux pattes d’ours',
      goal: 'L’enfant enchaîne 3 coups de poing (direct avant, direct arrière, crochet) sur une patte d’ours et revient en garde.',
      open: [
        'Objectif du cours, matériel et préparation en 5 minutes',
        'Vos premiers mots : « 1, c’est le direct avant, 2, le direct arrière, 3, le crochet. Prêts ? 1-2-3… en garde ! »',
        block,
      ],
      activity: [
        'Étapes techniques pour le crochet et l’enchaînement 1-2-3',
        'Comment les 10–12 ans tiennent les pattes d’ours pour un partenaire, en toute sécurité',
        'Fiche niveau d’âge pour les 4–6 ans, 7–9 ans et 10–12 ans',
      ],
      close: [
        '2 exercices aux pattes d’ours avec schémas, séries et durées : Appel des numéros et Partenaires aux pattes d’ours',
        'Jeu final : La mémoire des poings',
        'Échange sur la valeur (l’esprit d’équipe), mot aux parents et contrôle des barrettes',
      ],
    },
    {
      tab: 'Sécurité et dégagements',
      n: 'COURS 09 · SEMAINE 35',
      title: 'Se dégager d’une saisie et sécurité face aux inconnus',
      goal: 'L’enfant se dégage d’une saisie de poignet faite en douceur, recule, utilise sa voix forte et sait qu’il doit courir prévenir un adulte de confiance.',
      open: [
        'Objectif du cours, matériel et préparation en 5 minutes',
        'Vos premiers mots : se dégager, reculer, voix forte, courir vers un adulte de confiance',
        block,
      ],
      activity: [
        'Des étapes de dégagement simples, travaillées lentement et en douceur',
        'Les bons réflexes : rester avec son groupe, utiliser sa voix, prévenir un adulte de confiance',
        'Fiche niveau d’âge pour les 4–6 ans, 7–9 ans et 10–12 ans',
      ],
      close: [
        '2 mises en situation calmes avec schéma du tatami',
        'Un jeu final de 8 minutes avec schéma du tatami',
        'Échange sur la valeur (la sécurité), mot aux parents et contrôle des barrettes',
      ],
    },
  ],
});

// ── 4. FEUILLETEZ LE LIVRE (vraies pages, dans le même ordre que pageImages dans data/images.ts) ──
export const lookInside = fr({
  eyebrow: 'DE VRAIES PAGES. VOYEZ LE FORMAT AVANT D’ACHETER.',
  title: 'CE QUE VOUS AUREZ À CÔTÉ DU TATAMI.',
  sub: 'Ouvrez n’importe quelle page et lisez-la en grand : votre programme de 52 semaines, un cours complet en 3 pages, la répétition du passage de ceinture et votre fiche de préparation.',
  hint: 'Faites glisser pour voir la suite · Touchez une page pour l’agrandir',
  note: 'Ce sont des pages choisies, pas le livre complet.',
  captions: [
    'Votre programme de 52 semaines (T4)',
    'Cours 02 · plan du cours',
    'Cours 02 · enseigner',
    'Cours 02 · exercices et jeu',
    'Cours 10 · répétition du passage de ceinture',
    'Fiche de préparation + suivi des barrettes',
  ],
});

// ── 5. LE SYSTÈME ──────────────────────────────
export const plan = fr({
  eyebrow: 'UN SEUL SYSTÈME POUR TOUS VOS COURS',
  title: 'TOUTE L’ANNÉE EST PLANIFIÉE. VOUS N’AVEZ PLUS QU’À SALUER ET ENSEIGNER.',
  text: 'Chaque semaine de l’année est prévue : le début de l’un des 10 cours complets, un jeu ou un exercice de 10 minutes prêt à l’emploi, ou un contrôle des barrettes. Chaque cours vous donne le plan, les étapes techniques, les exercices et le jeu, en 3 pages.',
  steps: [
    { title: 'OUVRIR', text: 'Trouvez la semaine dans votre programme de 52 semaines.' },
    { title: 'PRÉPARER', text: 'Lisez la page et installez le tatami en 5 minutes.' },
    { title: 'ENSEIGNER', text: 'Jeu d’échauffement, une technique, pattes d’ours et travail à deux.' },
    { title: 'SALUT DE FIN', text: 'Jeu final, échange sur la valeur et mot aux parents.' },
  ],
  featuresTitle: 'DANS CHAQUE COURS COMPLET',
  features: [
    { icon: 'goal', title: 'Un objectif de cours clair', text: 'Une phrase sur ce que les enfants vont faire, la semaine où le lancer et ce qu’il faut avoir vu avant.' },
    {
      icon: 'timer',
      title: 'Un cours de 45 minutes chronométré',
      text: 'Salut et jeu d’échauffement 8 · Technique 12 · Exercices aux pattes d’ours et à deux 12 · Jeu final 8 · Retour au calme et valeur de la semaine 5.',
    },
    { icon: 'steps', title: 'Étapes techniques + consignes « À corriger »', text: 'Des étapes numérotées avec les points clés, et la consigne exacte pour corriger les erreurs les plus fréquentes.' },
    { icon: 'questions', title: '4–6 ans, 7–9 ans et 10–12 ans', text: 'Comment donner le même cours à tous les âges présents sur le tatami, même dans un groupe mélangé.' },
    { icon: 'quote', title: 'Vos mots, mot pour mot', text: 'Les premiers mots, les commandements et l’échange sur la valeur, plus une astuce de pro et une erreur à éviter.' },
    { icon: 'action', title: 'Exercices, jeu + mot aux parents', text: '2 exercices et un jeu final avec schémas du tatami, un contrôle des barrettes et un mot pour les parents.' },
  ],
} as const);

// ── 5b. VALEURS + NIVEAUX D’ÂGE ──────────────────
export const valuesBand = fr({
  eyebrow: 'BIEN PLUS QUE DES COUPS DE PIED ET DE POING',
  title: 'UNE VALEUR PAR COURS. TOUTE L’ANNÉE.',
  sub: 'Chaque cours se termine par un court échange sur la valeur, mot pour mot, pour que les parents voient ce que leurs enfants rapportent à la maison.',
  values: [
    { n: '01', value: 'Respect' },
    { n: '02', value: 'Concentration' },
    { n: '03', value: 'Maîtrise de soi' },
    { n: '04', value: 'Persévérance' },
    { n: '05', value: 'Confiance en soi' },
    { n: '06', value: 'Esprit d’équipe' },
    { n: '07', value: 'Vigilance' },
    { n: '08', value: 'Courage' },
    { n: '09', value: 'Sécurité' },
    { n: '10', value: 'Discipline' },
  ],
  agesTitle: 'CHAQUE COURS S’ADAPTE AUX ENFANTS QUE VOUS AVEZ DEVANT VOUS',
  ages: [
    { age: '4–6 ANS', text: 'Marques au sol, noms d’animaux, étapes courtes dites à voix haute et beaucoup de jeux.' },
    { age: '7–9 ANS', text: 'La technique complète sur les pattes d’ours du prof, en changeant de côté au signal.' },
    { age: '10–12 ANS', text: 'Plus de vitesse, déplacements en garde, tenir les pattes d’ours pour un partenaire et aider à mener le groupe.' },
  ],
});

// ── 6. LE PROGRAMME DE 52 SEMAINES (mêmes semaines et mêmes noms de jeux que le livre français) ──
export const yearPlan = fr({
  eyebrow: 'VOTRE PROGRAMME DE 52 SEMAINES',
  title: 'TOUTE L’ANNÉE, SEMAINE PAR SEMAINE.',
  sub: 'Les semaines ★ lancent un nouveau cours. Les semaines ◆ sont des contrôles des barrettes. Entre les deux, un jeu ou un exercice de 10 minutes prêt à l’emploi.',
  quarters: [
    {
      q: 'T1',
      weeks: 'SEMAINES 1–13',
      name: 'POSER LES BASES',
      full: [
        { week: '1', label: 'DÉBUT DU COURS 01', title: 'Garde, posture et respect du dojo' },
        { week: '5', label: 'DÉBUT DU COURS 02', title: 'Coups de poing directs : direct avant et direct arrière' },
        { week: '9', label: 'DÉBUT DU COURS 03', title: 'Coup de pied de face et équilibre' },
        { week: '13', label: 'CONTRÔLE DES BARRETTES', title: 'Faites le point avec la fiche de suivi', review: true },
      ],
      activities: '+ des jeux et exercices de 10 minutes prêts à l’emploi, comme « Le miroir », « Touche-épaule » et « La bulle ».',
    },
    {
      q: 'T2',
      weeks: 'SEMAINES 14–26',
      name: 'FRAPPER ET SE DÉFENDRE',
      full: [
        { week: '14', label: 'DÉBUT DU COURS 04', title: 'Coup de pied circulaire' },
        { week: '18', label: 'DÉBUT DU COURS 05', title: 'Blocages : haut, moyen et bas' },
        { week: '22', label: 'DÉBUT DU COURS 06', title: 'Enchaînements de poings aux pattes d’ours' },
        { week: '26', label: 'CONTRÔLE DES BARRETTES', title: 'Faites le point avec la fiche de suivi', review: true },
      ],
      activities: '+ des jeux et exercices de 10 minutes prêts à l’emploi, comme « Le cercle cible », « Le mur invisible » et « Combinaison 3 coups ».',
    },
    {
      q: 'T3',
      weeks: 'SEMAINES 27–39',
      name: 'BOUGER ET RESTER EN SÉCURITÉ',
      full: [
        { week: '27', label: 'DÉBUT DU COURS 07', title: 'Déplacements et distance' },
        { week: '31', label: 'DÉBUT DU COURS 08', title: 'Apprendre à chuter sans se faire mal' },
        { week: '35', label: 'DÉBUT DU COURS 09', title: 'Se dégager d’une saisie et sécurité face aux inconnus' },
        { week: '39', label: 'CONTRÔLE DES BARRETTES', title: 'Faites le point avec la fiche de suivi', review: true },
      ],
      activities: '+ des jeux et exercices de 10 minutes prêts à l’emploi, comme « Parcours malin », « La chute douce » et « Le cercle de sécurité ».',
    },
    {
      q: 'T4',
      weeks: 'SEMAINES 40–52',
      name: 'TOUT RASSEMBLER',
      full: [
        { week: '40', label: 'DÉBUT DU COURS 10', title: 'Répétition du passage de ceinture' },
        { week: '52', label: 'CONTRÔLE DES BARRETTES', title: 'Contrôle des barrettes de fin d’année', review: true },
      ],
      activities: '+ des jeux et exercices de 10 minutes prêts à l’emploi, comme « Le mini-kata en 8 mouvements », « Sous les projecteurs » et « Le relais du dojo ».',
    },
  ],
  toolsTitle: 'ET VOS OUTILS DE PROF',
  tools: [
    'Quand le cours se complique : 8 situations, quoi dire et quoi faire',
    'Fiche de préparation + suivi des barrettes : préparez n’importe quel cours en 5 minutes',
    'Fiche mémo du cours à garder près du tatami',
  ],
});

// ── 7. LES 10 COURS COMPLETS ─────────────────────────────────
export const meetings = fr({
  eyebrow: 'LES 10 COURS COMPLETS',
  title: 'DIX COURS ENFANTS. PRÊTS À DONNER.',
  sub: 'Suivez le programme de 52 semaines, ou commencez par le cours dont vos élèves ont besoin maintenant. Chacun tient en 3 pages : plan du cours, enseigner, exercices et jeu.',
  weekLabel: 'DÉBUT : SEMAINE',
  items: [
    { n: '01', week: '1', title: 'Garde, posture et respect du dojo', goal: 'Le salut, une garde solide les mains hautes et la position prêt au commandement.' },
    { n: '02', week: '5', title: 'Coups de poing directs : direct avant et direct arrière', goal: 'Un direct avant et un direct arrière depuis la garde, la main revenant devant le visage à chaque fois.' },
    { n: '03', week: '9', title: 'Coup de pied de face et équilibre', goal: 'Genou levé, frappe avec le bol du pied et retour au sol en garde, avec contrôle.' },
    { n: '04', week: '14', title: 'Coup de pied circulaire', goal: 'Pivoter sur le pied d’appui, genou sur le côté, et frapper une cible basse ou moyenne avec contrôle.' },
    { n: '05', week: '18', title: 'Blocages : haut, moyen et bas', goal: 'Trois blocages au commandement, les yeux sur le prof.' },
    { n: '06', week: '22', title: 'Enchaînements de poings aux pattes d’ours', goal: 'Un enchaînement de 3 coups de poing (direct avant, direct arrière, crochet) sur patte d’ours, puis retour en garde.' },
    { n: '07', week: '27', title: 'Déplacements et distance', goal: 'Avancer, reculer et se déplacer sur le côté sans croiser les pieds, et trouver la bonne distance.' },
    { n: '08', week: '31', title: 'Apprendre à chuter sans se faire mal', goal: 'Chute arrière et chute latérale depuis une position basse, menton rentré, puis relevé en sécurité.' },
    { n: '09', week: '35', title: 'Se dégager d’une saisie et sécurité face aux inconnus', goal: 'Se dégager d’une saisie de poignet faite en douceur, utiliser sa voix forte et courir vers un adulte de confiance.' },
    { n: '10', week: '40', title: 'Répétition du passage de ceinture', goal: 'Un passage de ceinture « blanc », au calme, avec les techniques de l’année, une grille d’évaluation et le déroulé de la cérémonie.' },
  ],
  cta: 'JE VEUX LES 10 COURS + 5 BONUS',
  ctaMicrocopy: `${prices.main} · Paiement unique`,
});

// ── 8. BONUS (même ordre et mêmes valeurs que le checkout) ──────────────
export const bonuses = fr({
  eyebrow: `5 BONUS INCLUS DANS VOTRE LIVRE À ${prices.main}`,
  title: 'TOUT LE RESTE DONT VOS COURS ENFANTS ONT BESOIN.',
  sub: 'Des jeux, des passages de ceinture, des messages aux parents, des exercices aux pattes d’ours et un cours d’essai, prêts à l’emploi.',
  valueLabel: 'VALEUR',
  free: 'OFFERT',
  totalNote: `${prices.bonusTotal} de bonus offerts`,
  items: [
    {
      label: 'BONUS 01',
      value: prices.bonuses[0],
      short: '60 jeux d’arts martiaux pour enfants',
      hook: 'Ne soyez plus jamais à court de jeux.',
      title: '60 jeux d’arts martiaux pour enfants — sans matériel',
      text: '60 jeux amusants et sans danger pour lancer, dynamiser et terminer n’importe quel cours, classés par objectif : échauffement, réaction, équilibre, concentration, coups de pied et de poing, esprit d’équipe et retour au calme. Chacun avec la mise en place, les règles, la durée et un conseil de sécurité.',
    },
    {
      label: 'BONUS 02',
      value: prices.bonuses[1],
      short: 'Kit passage de ceinture',
      hook: 'Des passages de ceinture calmes et bien organisés.',
      title: 'Kit passage de ceinture — critères, déroulé du jour J et diplômes à imprimer',
      text: 'Une liste de critères type pour chaque ceinture (à adapter à votre discipline), une grille d’évaluation, le déroulé minuté du jour J, quoi dire pour ouvrir et clôturer le passage, un suivi des barrettes et des diplômes à imprimer.',
    },
    {
      label: 'BONUS 03',
      value: prices.bonuses[2],
      short: 'Pack communication parents',
      hook: 'Sachez exactement quoi envoyer aux familles.',
      title: 'Pack communication parents — lettre de bienvenue, règlement du dojo et messages prêts à envoyer',
      text: 'Une lettre de bienvenue pour les nouveaux élèves, un règlement du dojo d’une page pour les enfants, la liste des affaires à apporter, des annonces de passage de ceinture, un mot mensuel sur les valeurs, des messages pour les absences et des formulations sereines pour parler aux parents.',
    },
    {
      label: 'BONUS 04',
      value: prices.bonuses[3],
      short: '50 exercices aux pattes d’ours et à deux',
      hook: 'Du travail aux pattes d’ours qui change à chaque cours.',
      title: '50 exercices aux pattes d’ours et à deux — prêts pour tous vos cours enfants',
      text: '50 exercices avec pattes d’ours, boucliers de frappe et partenaires pour les coups de poing, les coups de pied, les blocages, les enchaînements, les déplacements et la réaction. Chacun avec la mise en place, les séries et la durée, la façon de tenir la cible et une consigne de sécurité.',
    },
    {
      label: 'BONUS 05',
      value: prices.bonuses[4],
      short: 'Le guide du cours d’essai',
      hook: 'Faites de chaque premier cours un vrai succès.',
      title: 'Le guide du cours d’essai — un premier cours dont enfants et parents se souviennent',
      text: 'Comment accueillir la famille à l’entrée, le déroulé d’un cours d’essai de 30 minutes avec des jeux et une première technique que chaque enfant réussit, quoi dire à la fin et des messages de suivi prêts à envoyer.',
    },
  ],
});

// ── 9. OFFRE ─────────────────────────────────────────────────────
export const offer = fr({
  eyebrow: 'VOTRE PROCHAIN COURS COMMENCE ICI',
  title: 'TOUTE VOTRE ANNÉE DE COURS ENFANTS. DANS UN SEUL LIVRE.',
  kicker: 'LE LIVRE NUMÉRIQUE COMPLET',
  name: 'Arts Martiaux Enfants — Cours Prêts™',
  description:
    'Un programme de 52 semaines et 10 cours enfants complets de 45 minutes, prêts à donner, plus cinq bonus : des jeux, un kit passage de ceinture, des messages aux parents, des exercices aux pattes d’ours et un guide du cours d’essai.',
  mockupAlt: 'Tout ce qui est inclus dans Arts Martiaux Enfants — Cours Prêts™',
  includedTitle: 'VOTRE LIVRE CONTIENT',
  included: [
    'Programme de 52 semaines : toute l’année planifiée',
    '10 cours enfants complets, 3 pages chacun',
    'Un déroulé de 45 minutes chronométré dans chaque cours',
    'Étapes techniques, points clés et consignes « À corriger »',
    '4–6 ans, 7–9 ans et 10–12 ans dans chaque cours',
    'Exercices, jeu final, échange sur la valeur et mot aux parents',
    'Outils du prof : moments difficiles, fiche de préparation, fiche mémo',
  ],
  /** Récapitulatif de valeur : exactement les prix du checkout */
  stackTitle: 'TOUT CE QUE VOUS RECEVEZ',
  mainItem: { label: 'Arts Martiaux Enfants — Cours Prêts™ (programme de 52 semaines + 10 cours complets)', value: prices.anchor },
  totalLabel: 'Valeur totale',
  totalValue: prices.totalValue,
  todayLabel: 'AUJOURD’HUI',
  priceNote: `Paiement unique · Environ ${prices.perWeek} par semaine`,
  saveNote: `Vous économisez ${prices.savings}`,
  /** Le SEUL bouton qui mène au checkout */
  cta: `OUI, JE VEUX LE LIVRE — ${prices.main}`,
  ctaNote: ['Vers le paiement sécurisé', 'Accès envoyé par e-mail après le paiement'],
  format:
    'PDF en français. Livraison numérique. Adaptez chaque cours à votre discipline, aux règles de sécurité de votre club ainsi qu’à l’âge et au niveau de vos élèves.',
  guaranteeNote: 'Satisfait ou remboursé pendant 10 jours',
});

// ── 10. GARANTIE ────────────────────────────────────────────
export const guarantee = fr({
  sealTop: '10 JOURS',
  sealBottom: 'SATISFAIT OU REMBOURSÉ',
  title: 'SATISFAIT OU REMBOURSÉ PENDANT 10 JOURS. SANS AVOIR À VOUS JUSTIFIER.',
  text: 'Donnez vos prochains cours avec. S’il ne vous convient pas, écrivez à support@stockdigital.shop dans les 10 jours suivant votre achat et nous vous rembourserons.',
});

// ── 11. FAQ ───────────────────────────────────────────────────────
export const faq = fr({
  eyebrow: 'SACHEZ EXACTEMENT CE QUE VOUS ACHETEZ',
  title: 'QUELQUES QUESTIONS AVANT DE SALUER.',
  sub: 'Conçu pour les profs et les responsables de club qui donnent des cours aux enfants. Un livre de cours prêts à donner, pas une formation vidéo.',
  items: [
    {
      q: 'Qu’est-ce que c’est, exactement ?',
      a: 'Un livre PDF en français : un programme de 52 semaines (chaque semaine contient le début d’un cours, un jeu ou un exercice de 10 minutes prêt à l’emploi, ou un contrôle des barrettes) et 10 cours enfants complets de 45 minutes, de 3 pages chacun, plus des outils pour le prof et 5 bonus.',
    },
    {
      q: 'Pour quelles disciplines ?',
      a: 'Les disciplines de percussion enseignées aux enfants : karaté, taekwondo, kick-boxing, boxe thaï et programmes multi-styles. Les techniques portent des noms génériques (coup de pied de face, direct bras avant, blocage haut) et chaque cours vous rappelle d’utiliser les noms, l’étiquette et l’ordre des ceintures de votre discipline.',
    },
    {
      q: 'Pour quel âge ?',
      a: 'Les enfants d’environ 4 à 12 ans. Chaque cours a une fiche niveau d’âge qui explique comment l’enseigner aux 4–6 ans, 7–9 ans et 10–12 ans : il convient donc aussi aux groupes d’âges mélangés.',
    },
    {
      q: 'Y a-t-il du combat ou du contact ?',
      a: 'Pas de combat. Les enfants frappent sur des pattes d’ours, des cibles ou dans le vide. Les exercices à deux se font avec un contact léger et contrôlé, uniquement sur les pattes d’ours, toujours sous surveillance, et les chutes s’apprennent sur tapis, depuis des positions basses.',
    },
    {
      q: 'Le cours sur la sécurité enseigne-t-il l’autodéfense ?',
      a: 'Le cours 09 enseigne des dégagements de saisie simples et en douceur, et de bons réflexes : rester avec son groupe, utiliser sa voix forte et prévenir un adulte de confiance. Ce n’est pas une promesse qu’un enfant saura se défendre dans n’importe quelle situation.',
    },
    {
      q: 'Comment vais-je le recevoir ?',
      a: 'Une fois votre paiement validé, l’accès est envoyé à l’adresse e-mail saisie lors du paiement. Rien n’est expédié par la poste. Utilisez une adresse à laquelle vous avez accès et vérifiez vos spams si besoin.',
    },
    {
      q: 'Puis-je utiliser les cours séparément ?',
      a: 'Oui. Chaque cours est indépendant : vous pouvez commencer par celui dont vos élèves ont besoin maintenant, ou suivre le programme de 52 semaines dès la semaine 1.',
    },
    {
      q: 'Puis-je l’imprimer ?',
      a: 'Oui. Les pages se lisent facilement sur un téléphone ou une tablette posée près du tatami, et la fiche de préparation et le suivi des barrettes sont prêts à imprimer.',
    },
    {
      q: 'Puis-je le partager avec les autres profs de mon club ?',
      a: 'Cet achat est réservé à votre usage personnel en tant que prof. Le partager entre plusieurs clubs, le redistribuer ou le revendre n’est pas inclus dans cette offre.',
    },
    { q: 'Est-ce un abonnement ?', a: `Non. Un seul paiement de ${prices.main}.` },
    {
      q: 'Comment fonctionne la garantie ?',
      a: 'Satisfait ou remboursé pendant 10 jours : vous avez 10 jours pour demander un remboursement, sans avoir à vous justifier.',
    },
  ],
});

// ── 12. DERNIER APPEL ─────────────────────────────────────────────
export const finalCta = fr({
  eyebrow: 'POUR LE PROCHAIN « QU’EST-CE QUE JE LEUR FAIS FAIRE CE SOIR ? »',
  title: 'MONTEZ SUR LE TATAMI AVEC LE COURS DE CE SOIR DÉJÀ PRÊT.',
  text: 'Ouvrez la page de la semaine. Préparez en 5 minutes. Donnez un cours où les enfants auront envie de revenir.',
  microcopy: `Programme de 52 semaines + 10 cours complets + 5 bonus · ${prices.main}, paiement unique`,
});

// ── 13. PIED DE PAGE ──────────────────────────────────────────────────
export const footer = fr({
  line: 'ARTS MARTIAUX ENFANTS — COURS PRÊTS™ · Contenu pédagogique pour les profs. Respectez toujours les règles de votre discipline et les consignes de sécurité de votre club.',
  supportLabel: 'Assistance :',
  supportEmail: 'support@stockdigital.shop',
  copyright: '© 2026',
});
